import { IFeeInSale, IProductOnline } from "../types/types";
import { processFeesOnly } from "./fee.utils";
import { toFixed } from "./number.utils";

export function calculateProductTotal(
  items: IProductOnline[],
  tax?: number | string,
  storeCredits?: number | null,
  fees?: IFeeInSale[],
) {
  let total = 0;
  const salesTax = tax && !isNaN(+tax) ? +tax : 0;
  const storeCreditsAmount = storeCredits ?? 0;

  // fees should always be off item total, not gross total.
  const feeTotal = fees ? processFeesOnly(calculateItemTotal(items), fees) : 0;

  for (const item of items) {
    const itemPrice = item.price?.value ?? 0;
    if (item.salesTaxExcluded === true) {
      total = total + itemPrice;
    } else {
      total = total + itemPrice + (salesTax / 100) * itemPrice;
    }
  }

  if (storeCreditsAmount) {
    total = total - storeCreditsAmount;
    if (total < 0) {
      total = 0;
    }
  }

  total += feeTotal;

  return toFixed(total);
}

/**
 * GST variant: product total computed per item as
 * (price + (tax/100) * price), summed across items. The org sales tax
 * is ignored.
 * @param items
 * @param storeCredits - [optional] amount of store credits applied
 * @param fees - [optional] fees the store applies to online orders
 * @returns value upto 2 decimals
 */
export function calculateGstProductTotal(
  items: IProductOnline[],
  storeCredits?: number | null,
  fees?: IFeeInSale[],
) {
  let total = items.reduce((acc, item) => {
    const itemPrice = item.price?.value ?? 0;
    return acc + itemPrice + ((item.tax ?? 0) / 100) * itemPrice;
  }, 0);

  // fees should always be off item total, not gross total.
  const feeTotal = fees ? processFeesOnly(calculateItemTotal(items), fees) : 0;

  const storeCreditsAmount = storeCredits ?? 0;
  if (storeCreditsAmount) {
    total = total - storeCreditsAmount;
    if (total < 0) {
      total = 0;
    }
  }

  total += feeTotal;

  return toFixed(total);
}

/**
 * GST variant: each product is taxed at its own rate. Org sales tax is ignored.
 * @param items
 * @returns value upto 2 decimals
 */
export function calculateGstTaxTotal(items: IProductOnline[]) {
  const taxTotal = items.reduce(
    (acc, item) => acc + ((item.tax ?? 0) / 100) * (item.price?.value ?? 0),
    0,
  );
  return toFixed(taxTotal);
}

export function calculateItemTotal(items: IProductOnline[]) {
  const itemTotal: number = items.reduce(
    (acc, item) => +toFixed(acc + (item.price?.value ?? 0)),
    0,
  );
  return toFixed(itemTotal);
}

export function calculateTaxtotal(items: IProductOnline[], tax: number) {
  let taxTotal = 0;

  if (items) {
    for (const item of items) {
      const itemPrice = item.price?.value ?? 0;

      if (item.salesTaxExcluded !== true) {
        taxTotal = taxTotal + (tax / 100) * itemPrice;
      }
    }
  }

  return toFixed(taxTotal);
}
